import differenceInYears from 'date-fns/difference_in_years'
import messages from 'lang'
import every from 'lodash/every'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import trim from 'lodash/trim'
import { validate as postcodeValidate } from 'postal-codes-js'
import * as React from 'react'
import { Validator } from 'redux-form'

type ValidatorType = Validator

const email: ValidatorType = (value) => {
  if (isNil(value) || (typeof value === 'string' && isEmpty(value)) || typeof value !== 'string') {
    return { id: messages.validation_email }
  }

  return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i.test(value)
    ? undefined
    : { id: messages.validation_email }
}

const equalLength: (number) => ValidatorType = (length) => (value) => {
  if (typeof value === 'string' && value.length !== length) {
    return {
      id: messages.validation_equal_length,
      values: { length: `${length}` }
    }
  }

  return undefined
}

const isAlphanumeric: ValidatorType = (value) => {
  if (isNil(value) || (typeof value === 'string' && isEmpty(value)) || typeof value !== 'string') {
    return { id: messages.validation_name }
  }

  return /^[a-zA-ZĄĮĀĆÄÅĘĒČÉŹĖĢĶĪĻŠŃŅÓŌÕÖŲŁŚŪÜŻŽąįāćäåęēčéźėģķīļšńņóōõöųłśūüżž0-9 _.+-]*$]?/i.test(
    value
  )
    ? undefined
    : { id: messages.validation_name }
}

const isDifferentFrom: (fieldName: string) => ValidatorType = (target) => (value, values) => {
  if (values && values[target] === value) {
    return {
      id: messages.validation_different_value
    }
  }

  return undefined
}

const isIpValid = (value: string | void) => {
  if (typeof value === 'string' && !isNil(value)) {
    return new RegExp(
      /^((?:(?:(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)|%)\.){3}(?:(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)|%))$/
    ).test(value.trim())
  }
  return false
}

const isPostcodeValid: ValidatorType = (value, values) => {
  if (isNil(values)) return
  const countryCode = values.country
  if (isNil(countryCode) || typeof countryCode !== 'string') return
  return postcodeValidate(countryCode, value) === true
    ? undefined
    : postcodeValidate(countryCode, value)
}

const isSameAs: (fieldName: string) => ValidatorType = (target) => (value, values, __, field) => {
  if (values && values[target] !== value) {
    return {
      id: messages.validation_should_not_match,
      values: { field }
    }
  }

  return undefined
}

const smallerThan: (traget: string, a: React.ReactNode, b: React.ReactNode) => ValidatorType =
  (target, a, b) => (value, values) => {
    if (!values) return undefined

    const castedA = Number(values[target])
    const castedB = Number(value)
    if (!Number.isNaN(castedA) && !Number.isNaN(castedA) && castedA < castedB) {
      return {
        id: messages.validation_smaller_than,
        values: { a, b }
      }
    }

    return undefined
  }

const greaterThan: (target: string, a: React.ReactNode, b: React.ReactNode) => ValidatorType =
  (target, a, b) => (value, values) => {
    if (!values) return undefined

    const castedA = Number(values[target])
    const castedB = Number(value)
    if (!Number.isNaN(castedA) && !Number.isNaN(castedA) && castedA > castedB) {
      return {
        id: messages.validation_greater_than,
        values: { a, b }
      }
    }

    return undefined
  }

const maxValue: (max: number, field: React.ReactNode, canEqual?: boolean) => ValidatorType =
  (max, field, canEqual = false) =>
  (value) => {
    const numValue = Number(value)
    if (Number.isNaN(numValue)) return undefined

    if (numValue > max) {
      return {
        id: messages.validation_is_greater_than_max,
        values: { field, value: numValue }
      }
    }

    if (!canEqual && numValue === max) {
      return {
        id: messages.validation_smaller_than,
        values: { a: field, b: numValue }
      }
    }

    return undefined
  }

const minValue: (min: number, field: React.ReactNode, canEqual?: boolean) => ValidatorType =
  (min, field, canEqual = false) =>
  (value) => {
    const numValue = Number(value)
    if (Number.isNaN(numValue)) return undefined

    if (numValue <= min) {
      if (canEqual && numValue === min) return undefined
      return {
        id: messages.validation_is_less_than_min,
        values: { field, value: min }
      }
    }

    return undefined
  }

const isLength: (miin: number, field: React.ReactNode, canEqual?: boolean) => ValidatorType =
  (min, field, canEqual = true) =>
  (value) => {
    if (isNil(value)) return undefined
    const { length } = String(value)
    if (length === 0) return undefined
    if (length) {
      if (canEqual && length === min) return undefined
      return {
        id: messages.validation_is_length,
        values: { field, value: min }
      }
    }

    return undefined
  }

const minDifferenceInYearsToNow: (number: number) => ValidatorType = (diff) => (value, values) => {
  if (
    values &&
    !isNil(values.year) &&
    !isNil(values.month) &&
    !isNil(values.day) &&
    differenceInYears(
      new Date(),
      new Date(parseInt(values.year, 10), parseInt(values.month, 10), parseInt(values.day, 10))
    ) < diff
  ) {
    return {
      id: messages.validation_difference_in_years,
      values: { difference: `${diff}` }
    }
  }

  return undefined
}

const required: ValidatorType = (value) => {
  if (isNil(value)) {
    return { id: messages.validation_required }
  }

  if (typeof value === 'string') {
    const trimmed = trim(value)

    if (isEmpty(trimmed)) {
      return { id: messages.validation_required }
    }
  }

  return undefined
}

const mustBeEntered: (field: React.ReactNode) => ValidatorType =
  (label) => (value, values, form, field) => {
    const hasError = !isNil(required(value, values, form, field))

    if (hasError) {
      return { id: messages.validation_must_be_entered, values: { field: label } }
    }

    return undefined
  }

const name: ValidatorType = (value) => {
  if (isNil(value) || (typeof value === 'string' && isEmpty(value)) || typeof value !== 'string') {
    return { id: messages.validation_name }
  }

  return /['a-zA-ZĄĮĀĆÄÅĘĒČÉŹĖĢĶĪĻŠŃŅÓŌÕÖŲŁŚŪÜŻŽąįāćäåęēčéźėģķīļšńņóōõöųłśūüżž][a-zA-ZĄĮĀĆÄÅĘĒČÉŹĖĢĶĪĻŠŃŅÓŌÕÖŲŁŚŪÜŻŽąįāćäåęēčéźėģķīļšńņóōõöųłśūüżž \-,.']*[a-zA-ZĄĮĀĆÄÅĘĒČÉŹĖĢĶĪĻŠŃŅÓŌÕÖŲŁŚŪÜŻŽąįāćäåęēčéźėģķīļšńņóōõöųłśūüżž][.']?/i.test(
    value
  )
    ? undefined
    : { id: messages.validation_name }
}

const validIpAddress = (value: undefined | Array<string>) => {
  if (isNil(value) || isEmpty(value)) {
    return undefined
  }

  if (every(value, isIpValid)) {
    return undefined
  }

  return { id: messages.validation_ip_address }
}

const hasNoWhitespace = (value: undefined | string) => {
  if (isNil(value) || isEmpty(value)) {
    return undefined
  }

  if (/\s/.test(value)) {
    return { id: messages.validation_has_no_spaces }
  }

  return undefined
}

export default {
  email,
  equalLength,
  greaterThan,
  hasNoWhitespace,
  isAlphanumeric,
  isDifferentFrom,
  isIpValid,
  isLength,
  isPostcodeValid,
  isSameAs,
  maxValue,
  minDifferenceInYearsToNow,
  minValue,
  mustBeEntered,
  name,
  required,
  smallerThan,
  validIpAddress
}
